import { decryptCardField } from '@/infra/rain'
import {
  AdaptedGetKemCardEncryptedParams,
  AdaptedKemCardEncryptedDTO,
  KemCardEncryptedDTO,
} from './walletTypes'

/**
 * Adapts encrypted card response by decrypting sensitive fields
 *
 * This adapter handles the decryption of card number and CVV fields
 * that are encrypted with AES-GCM by the RAIN provider.
 *
 * Flow:
 * 1. Check if plaintext fields are available (non-RAIN providers)
 * 2. If not, decrypt the encrypted fields using the session secret key
 * 3. Return adapted DTO with decrypted values
 *
 * @param response - Raw encrypted card response from API
 * @param params - Parameters including the secret key for decryption
 * @returns Adapted DTO with decrypted card number and CVV
 */
export const adaptKemCardEncrypted = async (
  response: KemCardEncryptedDTO,
  params: AdaptedGetKemCardEncryptedParams,
): Promise<AdaptedKemCardEncryptedDTO> => {
  /**
   * Get decrypted card number
   * Falls back to plaintext if available (for non-RAIN providers)
   */
  const getCardNumberDecrypted = async (): Promise<string> => {
    // If plaintext card number is available, use it directly
    if (response?.card_number) {
      return response.card_number
    }

    // Decrypt the encrypted card number
    if (!response?.card_number_encrypted?.data || !response?.card_number_encrypted?.iv) {
      throw new Error('Card number encryption data is missing')
    }

    const cardNumberDecrypted = await decryptCardField(
      response.card_number_encrypted.data,
      response.card_number_encrypted.iv,
      params.secret_key,
    )

    return cardNumberDecrypted
  }

  /**
   * Get decrypted CVV
   * Falls back to plaintext if available (for non-RAIN providers)
   */
  const getCvvDecrypted = async (): Promise<string> => {
    // If plaintext CVV is available, use it directly
    if (response?.cvv) {
      return response.cvv
    }

    // Decrypt the encrypted CVV
    if (!response?.cvv_encrypted?.data || !response?.cvv_encrypted?.iv) {
      throw new Error('CVV encryption data is missing')
    }

    const cvvDecrypted = await decryptCardField(
      response.cvv_encrypted.data,
      response.cvv_encrypted.iv,
      params.secret_key,
    )

    return cvvDecrypted
  }

  // Decrypt both fields in parallel for better performance
  const [cardNumberDecrypted, cvvDecrypted] = await Promise.all([
    getCardNumberDecrypted(),
    getCvvDecrypted(),
  ])

  // Return adapted DTO without encrypted fields
  const { card_number_encrypted, cvv_encrypted, ...rest } = response

  return {
    ...rest,
    cardNumberDecrypted,
    cvvDecrypted,
  }
}

export const walletAdapter = {
  adaptKemCardEncrypted,
}
